import axios from 'axios';
import { 
  getPendingSyncs, 
  removePendingSync, 
  addPendingSync,
  setMetadata,
  getMetadata
} from './indexedDB';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Check if online
export function isOnline() {
  return navigator.onLine;
}

// Queue an operation for later sync
export async function queueOfflineOperation(operation) {
  const { method, endpoint, data, entityType, entityId } = operation;
  
  await addPendingSync({
    method,
    endpoint,
    data,
    entityType,
    entityId,
    createdAt: new Date().toISOString()
  });
  
  console.log('Operation queued for sync:', operation);
}

// Process all pending syncs
export async function processPendingSyncs() {
  if (!isOnline()) {
    console.log('Still offline, cannot sync');
    return { success: false, message: 'Hors ligne' };
  }

  const pendingSyncs = await getPendingSyncs();
  
  if (!pendingSyncs || pendingSyncs.length === 0) {
    console.log('No pending syncs');
    return { success: true, synced: 0 };
  }

  console.log(`Processing ${pendingSyncs.length} pending syncs`);
  
  let successCount = 0;
  let errorCount = 0;
  const errors = [];

  // Sort by timestamp to maintain order
  const sortedSyncs = pendingSyncs.sort((a, b) => a.timestamp - b.timestamp);

  for (const sync of sortedSyncs) {
    try {
      const url = `${API}${sync.endpoint}`;
      let response;

      switch (sync.method.toUpperCase()) {
        case 'POST':
          response = await axios.post(url, sync.data);
          break;
        case 'PUT':
          response = await axios.put(url, sync.data);
          break;
        case 'PATCH':
          response = await axios.patch(url, sync.data);
          break;
        case 'DELETE':
          response = await axios.delete(url);
          break;
        default:
          console.warn('Unknown method:', sync.method);
          continue;
      }

      if (response.status >= 200 && response.status < 300) {
        await removePendingSync(sync.id);
        successCount++;
        console.log('Synced successfully:', sync.endpoint);
      }
    } catch (error) {
      console.error('Sync error for:', sync.endpoint, error);
      errorCount++;
      errors.push({
        endpoint: sync.endpoint,
        error: error.message
      });
      
      // If it's a 404 or 409, remove from queue (data no longer valid)
      if (error.response?.status === 404 || error.response?.status === 409) {
        await removePendingSync(sync.id);
      }
    }
  }

  await setMetadata('lastSyncAttempt', Date.now());
  
  return {
    success: errorCount === 0,
    synced: successCount,
    failed: errorCount,
    errors
  };
}

// Register for background sync if supported
export async function registerBackgroundSync() {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register('sync-pending-requests');
      console.log('Background sync registered');
      return true;
    } catch (error) {
      console.error('Background sync registration failed:', error);
      return false;
    }
  }
  return false;
}

// Listen for online event and sync
export function setupOnlineListener(onSyncComplete) {
  const handleOnline = async () => {
    console.log('Back online, attempting sync...');
    const result = await processPendingSyncs();
    if (onSyncComplete) {
      onSyncComplete(result);
    }
  };

  window.addEventListener('online', handleOnline);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
  };
}

// Get last sync info
export async function getLastSyncInfo() {
  const lastSync = await getMetadata('lastSyncAttempt');
  const pendingSyncs = await getPendingSyncs();
  
  return {
    lastSyncAttempt: lastSync ? new Date(lastSync) : null,
    pendingCount: pendingSyncs?.length || 0
  };
}
